import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { FaArrowLeft, FaMapMarkerAlt, FaVideo } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import Badge from "../../components/Badge";
import Card from "../../components/Card";
import InfoRow from "../../components/InfoRow";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import { useAuth } from "../../context/AuthContext";
import { convertTimeStringFromUTC } from "../../utils/dateUtils";
import { getStatusVariant } from "../../utils/styleUtils";
import { bookingService } from "../../services/bookingService";

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadBooking = useCallback(async () => {
    try {
      const response = await bookingService.getById(id);
      setBooking(response.booking);
    } catch (error) {
      console.error("Error loading booking:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadBooking();
  }, [loadBooking]);

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  if (!booking) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <p className="text-text/70 text-lg">Booking not found</p>
          <Link
            to={`/${user.role}/dashboard`}
            className="mt-4 inline-flex items-center gap-2 text-primary hover:opacity-80"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card>
        <PageHeader title="Booking Details" />

        <div className="space-y-4">
          <InfoRow label="Subject">
            {booking.subjectId?.name || "Unknown"}
          </InfoRow>

          <InfoRow label="Status">
            <Badge
              label={booking.status.replace("_", " ").toUpperCase()}
              variant={getStatusVariant(booking.status)}
              size="small"
              className="font-medium capitalize"
            />
          </InfoRow>

          <InfoRow label="Student">
            {booking.studentId?.name?.first} {booking.studentId?.name?.last}
          </InfoRow>

          {booking.assignedTutorId && (
            <InfoRow label="Assigned Tutor">
              {booking.assignedTutorId?.name?.first}{" "}
              {booking.assignedTutorId?.name?.last}
            </InfoRow>
          )}

          <InfoRow label="Preferred Times">
            <ul className="list-disc list-inside text-text/90 space-y-1">
              {booking.preferredTimes?.map((time, idx) => (
                <li key={idx}>
                  {dayjs.utc(time.date).local().format("MMM D, YYYY")} •{" "}
                  {convertTimeStringFromUTC(time.start)} -{" "}
                  {convertTimeStringFromUTC(time.end)}
                </li>
              ))}
            </ul>
          </InfoRow>

          <InfoRow label="Session Type">
            <span className="flex items-center gap-2">
              {booking.sessionType === "in-person" ? (
                <>
                  <FaMapMarkerAlt className="flex-shrink-0" /> In-Person
                </>
              ) : (
                <>
                  <FaVideo className="flex-shrink-0" /> Online (Google Meet)
                </>
              )}
            </span>
          </InfoRow>

          {booking.sessionType === "in-person" && booking.location && (
            <InfoRow label="Location">{booking.location}</InfoRow>
          )}

          {booking.expiresAt && (
            <InfoRow label="Expires">
              {dayjs
                .utc(booking.expiresAt)
                .local()
                .format("MMM D, YYYY h:mm A")}
            </InfoRow>
          )}

          {booking.assignedSessionId && (
            <InfoRow label="Session">
              <Link
                to={`/sessions/${booking.assignedSessionId}`}
                className="text-primary hover:opacity-80 font-medium"
              >
                View Session →
              </Link>
            </InfoRow>
          )}

          {booking.remarks && (
            <InfoRow label="Remarks">{booking.remarks}</InfoRow>
          )}

          {booking.topicsToFocus && (
            <InfoRow label="Topics/Areas to Focus">
              <div className="text-text/90 space-y-1 whitespace-pre-wrap">
                {booking.topicsToFocus}
              </div>
            </InfoRow>
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <Link
            to={user?.role ? `/${user.role}/dashboard` : "/"}
            className="text-primary hover:opacity-80 font-medium flex items-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            {user?.role === "admin" ? "Back to Bookings" : "Back to Dashboard"}
          </Link>
        </div>
      </Card>
    </div>
  );
}
